import { format, addDays, startOfWeek, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const FASCE_LABELS = {
  mattina: "08:00 - 13:00 Mattina",
  pomeriggio: "13:00 - 18:00 Pomeriggio",
  intera: "Giornata intera",
};

export const DASHBOARD_VIEW_OPTIONS = [
  { id: "giorno", label: "Giorno" },
  { id: "settimana", label: "Settimana" },
];

function cellColor(stato) {
  if (stato === "pieno") return "bg-red-100 text-red-800 border-red-200";
  if (stato === "quasi_pieno") return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-emerald-50 text-emerald-800 border-emerald-200";
}

export default function CdAgendaGrid({
  giorni = [],
  capacita = 20,
  view = "settimana",
  onViewChange,
  showViewToggle = false,
  viewOptions = DASHBOARD_VIEW_OPTIONS,
  showPeriodNav = false,
  periodLabel,
  onPrevPeriod,
  onNextPeriod,
  /** @deprecated Usare showPeriodNav */
  showWeekNav = false,
  weekLabel,
  onPrevWeek,
  onNextWeek,
  agendaLink = "/centro-diurno/agenda",
}) {
  const fasce = ["mattina", "pomeriggio", "intera"];
  const navVisible = showPeriodNav || showWeekNav;
  const navLabel = periodLabel || weekLabel;
  const handlePrev = onPrevPeriod || onPrevWeek;
  const handleNext = onNextPeriod || onNextWeek;
  const showNav = navVisible && view !== "giorno" && navLabel && handlePrev && handleNext;

  return (
    <Card className="shadow-sm border-slate-200 min-w-0">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base font-semibold text-slate-800">Agenda Prenotazioni</CardTitle>
            <p className="text-xs text-slate-500 mt-0.5">Capienza max {capacita} ospiti contemporanei per fascia</p>
            {showNav && (
              <div className="flex items-center gap-1 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={handlePrev}
                  aria-label="Settimana precedente"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs font-medium text-slate-600 px-1 capitalize">{navLabel}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={handleNext}
                  aria-label="Settimana successiva"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
          {showViewToggle && onViewChange && (
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 shrink-0">
              {viewOptions.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => onViewChange(id)}
                  className={cn(
                    "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                    view === id
                      ? "bg-emerald-100 text-emerald-800 shadow-sm"
                      : "text-slate-500 hover:text-slate-700"
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0 min-w-0">
        <div className="overflow-x-auto overscroll-x-contain max-w-full rounded-md">
          <table className="w-full min-w-[520px] text-sm border-collapse">
          <thead>
            <tr>
              <th className="text-left py-2 px-2 text-xs text-slate-500 font-medium whitespace-nowrap">
                Fascia
              </th>
              {giorni.map((g) => (
                <th
                  key={g.data}
                  className="text-center py-2 px-1 text-xs text-slate-500 font-medium"
                  title={format(parseISO(g.data), "EEEE d MMMM yyyy", { locale: it })}
                >
                  {format(parseISO(g.data), "EEE d/M", { locale: it }).toUpperCase()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fasce.map((fascia) => (
              <tr key={fascia}>
                <td className="py-2 px-2 text-xs text-slate-600 whitespace-nowrap">{FASCE_LABELS[fascia]}</td>
                {giorni.map((g) => {
                  const cell = g.fasce?.[fascia] || {};
                  return (
                    <td key={`${g.data}-${fascia}`} className="p-1">
                      <div
                        className={`rounded-lg border text-center py-2 px-1 text-xs font-semibold whitespace-nowrap ${cellColor(cell.stato)}`}
                      >
                        {cell.occupati ?? 0}/{capacita}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3 text-[11px] text-slate-500">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-200" /> Disponibile
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-amber-100 border border-amber-200" /> Quasi pieno
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-red-100 border border-red-200" /> Completo
          </span>
          {agendaLink && (
            <Link
              to={agendaLink}
              className="ml-auto text-emerald-600 hover:text-emerald-700 font-medium"
            >
              Vai al calendario completo →
            </Link>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function CdKpiCard({ label, value, sub, accent = "emerald" }) {
  const colors = {
    emerald: "from-emerald-500 to-emerald-700",
    blue: "from-blue-500 to-blue-700",
    amber: "from-amber-500 to-amber-600",
    slate: "from-slate-500 to-slate-700",
  };
  return (
    <Card className="shadow-sm border-slate-200 overflow-hidden">
      <CardContent className="p-4">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
        <p className={`text-3xl font-bold mt-1 bg-gradient-to-r ${colors[accent] || colors.emerald} bg-clip-text text-transparent`}>
          {value}
        </p>
        {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

/** Giorni da mostrare in agenda: Lun–Ven; se la data selezionata è Sab/Dom include anche il weekend. */
export function agendaDaysFromDate(dateStr) {
  const d = parseISO(dateStr);
  const start = startOfWeek(d, { weekStartsOn: 1 });
  const week = Array.from({ length: 7 }, (_, i) => format(addDays(start, i), "yyyy-MM-dd"));
  const dow = d.getDay();
  if (dow === 0 || dow === 6) {
    return week;
  }
  return week.slice(0, 5);
}

/** Intervallo [da, a] per la vista agenda (giorno / settimana). */
export function agendaRangeFromView(dateStr, view = "settimana") {
  if (view === "giorno") {
    return [dateStr, dateStr];
  }
  const days = agendaDaysFromDate(dateStr);
  return [days[0], days[days.length - 1]];
}

/** Etichetta periodo settimana per navigazione agenda. */
export function agendaWeekLabel(dateStr) {
  const days = agendaDaysFromDate(dateStr);
  const start = parseISO(days[0]);
  const end = parseISO(days[days.length - 1]);
  return `${format(start, "d MMM", { locale: it })} – ${format(end, "d MMM yyyy", { locale: it })}`;
}

/** Etichetta periodo in base alla vista corrente. */
export function agendaPeriodLabel(dateStr, view = "settimana") {
  if (view === "giorno") return format(parseISO(dateStr), "d MMMM yyyy", { locale: it });
  return agendaWeekLabel(dateStr);
}

/** @deprecated Usare agendaDaysFromDate */
export function weekRangeFromDate(dateStr) {
  return agendaDaysFromDate(dateStr);
}
